import React, { useState, useEffect } from "react";
import { Spin, message, Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "../../../services/api";
import MoodQuestionnaire from "./MoodQuestionnaire";
import { MoodOption } from "./MoodMeterDashboard";

interface CompanyType {
    id: string | null;
    type: string;
    name: string;
}

const MoodPopup: React.FC = () => {
    const location = useLocation();
    const signature = localStorage.getItem("signature");
    const user = useSelector((state: any) => {
        return state.user.user;
    });

    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedCompany, setSelectedCompany] = useState<CompanyType>({
        id: null,
        type: "",
        name: "",
    });
    const [submittedTodayIds, setSubmittedTodayIds] = useState<string[]>([]);
    const [allTodayQuestionIds, setAllTodayQuestionIds] = useState<string[]>([]);
    const [showQuestionnaire, setShowQuestionnaire] = useState(false);
    const [reminderTimer, setReminderTimer] = useState<NodeJS.Timeout | null>(null);
    const [hasPendingQuestions, setHasPendingQuestions] = useState(false);

    const currentUserId = user?.userDetails?.id;
    const userName = user?.userDetails?.fname;
    const departmentId = user?.userDetails?.department;

    // Routes where popup should NOT appear
    const disabledRoutes = [
        "/login",
        "/register",
        "/forgot-password",
        "/two-step-verification",
        "/public/l/forms",
        "/application/mood-questionnaire",
    ];

    const isDisabledRoute = disabledRoutes.some(
        (route) => location.pathname === route || location.pathname.startsWith(route)
    );

    // Update selectedCompany when user data is available
    useEffect(() => {
        if (user?.userDetails?.company_id) {
            setSelectedCompany({
                id: user.userDetails.company_id,
                type: user.userDetails.account_type,
                name: user.userDetails.company_name || "",
            });
            setLoading(false);
        }
    }, [user]);

    // Clear reminder timer on unmount
    useEffect(() => {
        return () => {
            if (reminderTimer) {
                clearTimeout(reminderTimer);
            }
        };
    }, [reminderTimer]);

    const scheduleReminder = () => {
        if (reminderTimer) {
            clearTimeout(reminderTimer);
            setReminderTimer(null);
        }

        if (!visible && hasPendingQuestions && !isDisabledRoute) {
            const timer = setTimeout(() => {
                checkSubmissionStatus(true);
            }, 1 * 60 * 1000); // 1 minutes

            setReminderTimer(timer);
        }
    };

    const getPendingQuestions = (allQuestionIds: string[], submittedIds: string[]) => {
        return allQuestionIds.filter(id => !submittedIds.includes(id));
    };

    const handleClosePopup = () => {
        setVisible(false);
        setShowQuestionnaire(false);

        const today = new Date().toDateString();
        const now = new Date().toISOString();

        localStorage.setItem('moodPopupDismissed', today);
        localStorage.setItem('moodPopupDismissedTime', now); // Store exact time

        // Schedule reminder for later
        scheduleReminder();
    };

    const checkSubmissionStatus = async (forceShow = false) => {
        if (!selectedCompany.id || !currentUserId || !signature) {
            return;
        }

        try {
            // 1. Get submitted questions for today
            const submittedResponse = await api.post("userService/getSubmittedQuestionIds", {
                signature,
            });

            let submittedIds: string[] = [];
            if (submittedResponse.data?.message?.[0]?.status === true) {
                const data = submittedResponse.data.message[0].data;
                submittedIds = data.submitted_today_ids || [];
                setSubmittedTodayIds(submittedIds);
            }

            // 2. Get ALL today's questions (including newly added ones)
            const questionsResponse = await api.post('userService/getTodayQuestions', {
                signature,
                company_id: selectedCompany.id,
                company_type: selectedCompany.type,
            });

            const questions = questionsResponse.data?.message?.[0]?.data || [];

            // Check if there are ANY questions at all
            if (questions.length === 0) {
                setHasPendingQuestions(false);
                setVisible(false);
                return;
            }

            const allQuestionIds = questions.map((q: any) => q.id.toString());
            setAllTodayQuestionIds(allQuestionIds);

            // 3. Calculate pending questions
            const pendingQuestionIds = getPendingQuestions(allQuestionIds, submittedIds);
            const hasPending = pendingQuestionIds.length > 0;

            setHasPendingQuestions(hasPending);

            if (hasPending && !isDisabledRoute) {
                const today = new Date().toDateString();
                const lastShownDate = localStorage.getItem('moodPopupLastShown');
                const dismissedDate = localStorage.getItem('moodPopupDismissed');
                const wasDismissedToday = dismissedDate === today;

                let shouldShowPopup = false;

                if (forceShow) {
                    shouldShowPopup = true;
                }
                else if (lastShownDate !== today && !wasDismissedToday) {
                    shouldShowPopup = true;
                }
                else if (wasDismissedToday) {
                    const lastDismissedTime = localStorage.getItem('moodPopupDismissedTime');
                    if (lastDismissedTime) {
                        const dismissedTime = new Date(lastDismissedTime).getTime();
                        const currentTime = new Date().getTime();
                        const minutesElapsed = (currentTime - dismissedTime) / (1000 * 60);

                        if (minutesElapsed >= 5) {
                            shouldShowPopup = true;
                            localStorage.removeItem('moodPopupDismissed');
                            localStorage.removeItem('moodPopupDismissedTime');
                        }
                    } else {
                        shouldShowPopup = true;
                        localStorage.removeItem('moodPopupDismissed');
                    }
                }
                else {
                    const storedData = localStorage.getItem('moodPopupData');
                    if (storedData) {
                        try {
                            const lastPendingCount = JSON.parse(storedData).pendingCount || 0;
                            if (pendingQuestionIds.length > lastPendingCount) {
                                shouldShowPopup = true;
                            }
                        } catch (e) {
                            console.error("Error parsing stored data", e);
                        }
                    }
                }

                // Store current pending count for comparison
                localStorage.setItem('moodPopupData', JSON.stringify({
                    date: today,
                    pendingCount: pendingQuestionIds.length,
                    pendingIds: pendingQuestionIds
                }));

                if (shouldShowPopup) {
                    if (reminderTimer) {
                        clearTimeout(reminderTimer);
                        setReminderTimer(null);
                    }

                    setVisible(true);
                    localStorage.setItem('moodPopupLastShown', today);
                    localStorage.removeItem('moodPopupDismissed');
                    localStorage.removeItem('moodPopupDismissedTime');
                } else {
                    scheduleReminder();
                }
            } else {
                if (reminderTimer) {
                    clearTimeout(reminderTimer);
                    setReminderTimer(null);
                }
                setVisible(false);
            }

        } catch (error) {
            console.error("Error checking submission:", error);
        }
    };

    // Handle mood submission
    const handleMoodSubmit = async (response: {
        questionId: string;
        selectedOption: MoodOption;
        note?: string;
    }) => {
        if (!selectedCompany.id) {
            message.error("Please select a company first");
            return;
        }

        try {
            const payload = {
                signature,
                question_id: response.questionId,
                selected_option_id: response.selectedOption.id,
                mood_text: response.selectedOption.text,
                score: response.selectedOption.score,
                note: response.note || "",
                department_id: departmentId,
                company_id: selectedCompany.id,
                company_type: selectedCompany.type,
                is_anonymous: true,
            };

            const apiResponse = await api.post("userService/submitMoodResponse", payload);

            if (apiResponse.data?.message?.[0]?.status === true) {
                message.success({
                    content: "Mood submitted successfully!",
                    icon: "😊",
                    duration: 2,
                });

                // Update submitted IDs
                const newSubmittedIds = [...submittedTodayIds, response.questionId];
                setSubmittedTodayIds(newSubmittedIds);

                // Check remaining questions
                const remainingQuestions = getPendingQuestions(allTodayQuestionIds, newSubmittedIds);

                if (remainingQuestions.length === 0) {
                    // All questions completed
                    setVisible(false);
                    setShowQuestionnaire(false);
                    setHasPendingQuestions(false);
                    message.success("Thank you for completing today's mood check-in! 🎉");

                    // Clear stored data
                    localStorage.removeItem('moodPopupData');
                    localStorage.removeItem('moodPopupDismissed');

                    // Clear any scheduled reminders
                    if (reminderTimer) {
                        clearTimeout(reminderTimer);
                        setReminderTimer(null);
                    }
                } else {
                    // Still have pending questions
                    message.info(`You have ${remainingQuestions.length} more question${remainingQuestions.length > 1 ? 's' : ''} to answer`);

                    // Keep popup open for next question
                    setVisible(true);
                    setShowQuestionnaire(true);

                    // Schedule reminder in case user closes popup
                    scheduleReminder();
                }
            } else {
                message.error(apiResponse.data?.msg || "Failed to submit mood");
            }
        } catch (error: any) {
            console.error("Error submitting mood:", error);
            message.error("Error submitting mood. Please try again.");
        }
    };

    // Handle all questions completed
    const handleAllQuestionsCompleted = () => {
        setVisible(false);
        setShowQuestionnaire(false);
        setHasPendingQuestions(false);
        message.success("Thank you for completing today's mood check-in! 🎉");

        // Clear stored data
        localStorage.removeItem('moodPopupData');
        localStorage.removeItem('moodPopupDismissed');

        // Clear any scheduled reminders
        if (reminderTimer) {
            clearTimeout(reminderTimer);
            setReminderTimer(null);
        }
    };

    // Check on mount, route change, and periodically
    useEffect(() => {
        if (!isDisabledRoute && selectedCompany.id && currentUserId && !loading) {
            checkSubmissionStatus();
        }
    }, [location.pathname, selectedCompany.id, currentUserId, loading]);

    useEffect(() => {
        if (!isDisabledRoute && selectedCompany.id && !loading) {
            const interval = setInterval(() => {
                checkSubmissionStatus();
                // }, 1 * 60 * 1000); // Check every 1 minutes
            }, 60 * 60 * 1000); // Check every 1 hour

            return () => clearInterval(interval);
        }
    }, [selectedCompany.id, isDisabledRoute, loading]);

    if (loading) {
        return null;
    }

    if (isDisabledRoute) {
        return null;
    }

    if (!selectedCompany.id) {
        return null;
    }

    if (!visible) {
        return null;
    }

    if (!showQuestionnaire) {
        return (
            <div style={styles.container}>
                <div style={styles.popup}>
                    <button
                        onClick={handleClosePopup}
                        style={styles.closeButton}
                        aria-label="Close"
                    >
                        <CloseOutlined style={{ fontSize: '14px', color: '#999' }} />
                    </button>

                    <img
                        src="../../assets/img/mood_avatar.svg"
                        alt="Mood Check"
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                        }}
                        style={styles.image}
                    />
                    <div style={styles.bubble}>
                        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                            🌟 Time for Mood Check-in!
                        </div>
                        <div style={{ fontSize: '13px', marginBottom: '10px' }}>
                            How are you feeling today? Your anonymous response helps us understand team well-being.
                        </div>
                        <button
                            onClick={() => setShowQuestionnaire(true)}
                            style={{
                                background: '#4caf50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '20px',
                                padding: '8px 16px',
                                fontSize: '13px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                marginTop: '5px',
                                width: '100%',
                                justifyContent: 'center'
                            }}
                        >
                            <span>😊</span>
                            Check In Now
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Questionnaire popup
    return (
        <div style={styles.container}>
            <div style={{
                ...styles.popup,
                width: '450px',
                maxWidth: '90vw',
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                alignItems: 'flex-start',
                position: 'relative'
            }}>
                {/* Close Button for Questionnaire */}
                <button
                    onClick={handleClosePopup}
                    style={{
                        ...styles.closeButton,
                        top: '16px',
                        right: '16px',
                        backgroundColor: '#f5f5f5',
                        border: 'none',
                    }}
                    aria-label="Close"
                >
                    <CloseOutlined style={{ fontSize: '14px', color: '#666' }} />
                </button>

                <div style={{ width: '100%' }}>
                    <MoodQuestionnaire
                        onSubmit={handleMoodSubmit}
                        selectedCompany={selectedCompany}
                        currentUser={{
                            id: currentUserId,
                            name: userName,
                            department_id: departmentId,
                        }}
                        departments={[]}
                        disabled={false}
                        submittedTodayIds={submittedTodayIds}
                        onAllQuestionsCompleted={handleAllQuestionsCompleted}
                    />
                </div>
            </div>
        </div>
    );
};

const styles: {
    container: React.CSSProperties;
    popup: React.CSSProperties;
    bubble: React.CSSProperties;
    image: React.CSSProperties;
    closeButton: React.CSSProperties;
} = {
    container: {
        position: "fixed",
        bottom: "20px",
        right: "20px",
        zIndex: 9999,
    },
    popup: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        animation: "slideUp 0.6s ease-out",
    },
    image: {
        width: "80px",
        height: "80px",
        cursor: "pointer",
    },
    bubble: {
        marginLeft: "10px",
        background: "#fff9c4",
        borderRadius: "10px",
        padding: "15px",
        maxWidth: "300px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        fontSize: "14px",
        color: "#333",
        position: "relative",
    },
    closeButton: {
        position: "absolute",
        top: "-8px",
        right: "-8px",
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        backgroundColor: "white",
        border: "1px solid #e8e8e8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        zIndex: 10000,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        transition: "all 0.2s ease",
    },
};

// Add keyframes animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes slideUp {
        from {
            opacity: 0;
            transform: translateY(20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    .close-button:hover {
        background-color: #f5f5f5;
        border-color: #d9d9d9;
    }
`;
document.head.appendChild(styleSheet);

export default MoodPopup;